import { SCREENS } from "@stardust/constants";
import { isScrollLocked } from "@stardust/scroll-lock";

/**
 * Desktop Navigation - Hover/focus dropdown menus and scroll-based header visibility
 * Works with <details>/<summary> elements rendered by _navigation-item.liquid.
 * Mobile navigation is handled by mobile-menu-drawer.js
 */

const SHOW_DELAY = 200;
const HIDE_DELAY = 200;
const VIEWPORT_PADDING = 10;

/**
 * True when the device supports touch input (iPad, touch laptop, phone in desktop UA, etc.).
 * Used to switch top-level nav links from hover-and-navigate to tap-to-open, tap-again-to-navigate.
 */
const isTouchCapable = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;

class DesktopMenu {
  /**
   * @param {HTMLDetailsElement} details  – the <details> wrapper
   * @param {Navigation}        navigation
   */
  constructor(details, navigation) {
    this.details = details;
    this.summary = details.querySelector("summary");
    this.panel = details.querySelector(`#nav-menu-${details.id}`);
    this.navigation = navigation;
    this.open = false;
    this.hovered = { button: false, items: false };
    this.hideTimeout = null;
    this.showTimeout = null;
    this.isContentWidth = this.panel?.dataset.layoutWidth === "content";

    if (!this.summary || !this.panel) return;

    // Panel starts closed — block keyboard/AT access to its children until shown.
    // (CSS visibility:hidden also removes it from the tab order, but the resting
    // inert keeps state explicit and mirrors BaseDrawer's constructor.)
    this.panel.setAttribute('inert', '');

    this._bindEvents();
  }

  _bindEvents() {
    // Native <details> toggle (e.g. mobile tap on the summary background, or any path
    // that bypasses the JS show/hide) should keep aria-expanded on the summary in sync.
    this.details.addEventListener("toggle", () => {
      this.summary.setAttribute("aria-expanded", String(this.details.open));
    });

    // Only the desktop nav is initialized here — the mobile drawer uses an accordion.
    // The desktop container is display:none below the `nav` breakpoint, so click events
    // never reach this handler at mobile widths.
    //   - Click on summary background → prevent native <details> toggle (hover/focus open).
    //   - Click on inner <a> link → navigate normally; on touch, first tap opens, second navigates.
    this.summary.addEventListener("click", (e) => {
      if (!e.target.closest("a[href]")) {
        e.preventDefault();
      } else if (isTouchCapable && !this.open) {
        e.preventDefault();
        this.navigation.closeAllExcept(this);
        this.show(true);
      }
    });

    // Hover on summary/panel — skipped on touch devices (synthesized mouse events cause open+close flicker)
    if (!isTouchCapable) {
      this.summary.addEventListener("mouseenter", () => this._onEnter("button"));
      this.summary.addEventListener("mouseleave", () => this._onLeave("button"));
      this.panel.addEventListener("mouseenter", () => this._onEnter("items"));
      this.panel.addEventListener("mouseleave", () => this._onLeave("items"));
    }

    // Remove the top-level category <a> from the tab order — it shares visual
    // space with the <summary> and creates a phantom second tab stop.
    // This is done in JS (not the Liquid template) so the link remains reachable
    // without JS. The Enter handler below provides the keyboard navigation path.
    const categoryLink = this.summary.querySelector("a[href]");
    if (categoryLink) categoryLink.setAttribute("tabindex", "-1");

    // Enter on summary navigates to the top-level category link.
    // Uses click() to preserve target, download, and any click handlers on the link.
    this.summary.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && categoryLink) {
        e.preventDefault();
        categoryLink.click();
      }
    });

    // Keyboard focus on summary / panel (always active for accessibility)
    this.summary.addEventListener("focus", () => this._onEnter("button"));
    this.summary.addEventListener("blur", () => this._onLeave("button"));
    this.panel.addEventListener("focusin", () => this._onEnter("items"));
    this.panel.addEventListener("focusout", (e) => {
      // relatedTarget === null means focus dropped to nowhere rather than moving to a
      // specific element outside the panel. That happens when a focused control inside
      // the panel becomes disabled — e.g. a slider arrow at the end of its track. That
      // isn't the user leaving the menu, so keep it open; hover, ESC, and outside-tap
      // still close it. Tabbing to a real element outside (non-null) closes as before.
      if (!e.relatedTarget) return;
      if (!this.panel.contains(e.relatedTarget) && e.relatedTarget !== this.summary) {
        this._onLeave("items");
      }
    });
  }

  _onEnter(source) {
    this._clearHideTimeout();
    this._clearShowTimeout();
    this.hovered[source] = true;

    if (source === "button") {
      // If switching from another open menu, open instantly (no delay)
      const hadOpen = this.navigation.closeAllExcept(this);
      if (hadOpen) {
        this.show(true);
        return;
      }
    }

    // If panel is already open (e.g. mouse moved into items), show immediately
    if (this.open) return;

    this._scheduleShow();
  }

  _onLeave(source) {
    this.hovered[source] = false;
    this._clearShowTimeout();
    this._scheduleHide();
  }

  _clearShowTimeout() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  _clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  _scheduleShow() {
    this._clearShowTimeout();
    this.showTimeout = setTimeout(() => {
      if (this.hovered.button || this.hovered.items) {
        this.show();
      }
    }, SHOW_DELAY);
  }

  _scheduleHide() {
    this._clearHideTimeout();
    this.hideTimeout = setTimeout(() => {
      if (!this.hovered.button && !this.hovered.items) {
        this.hide();
      }
    }, HIDE_DELAY);
  }

  _adjustOverflow() {
    if (!this.isContentWidth) return;

    this.panel.style.marginLeft = "";

    const { left, right } = this.panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    if (right > viewportWidth - VIEWPORT_PADDING) {
      this.panel.style.marginLeft = `${viewportWidth - VIEWPORT_PADDING - right}px`;
    } else if (left < VIEWPORT_PADDING) {
      this.panel.style.marginLeft = `${VIEWPORT_PADDING - left}px`;
    }
  }

  show(instant = false) {
    if (this.open) return;

    // Open the <details> first so the panel is laid out, then measure & shift it
    // synchronously before adding is-visible. Otherwise the panel paints at the
    // wrong position for one frame before the overflow correction lands.
    this.details.open = true;
    this.panel.removeAttribute('inert');
    this._adjustOverflow();

    if (instant) {
      this.panel.classList.add("is-visible");
    } else {
      requestAnimationFrame(() => this.panel.classList.add("is-visible"));
    }

    this.open = true;
    // aria-expanded is synced by the toggle listener bound in _bindEvents.
  }

  hide(instant = false) {
    if (!this.open) return;
    if (!instant && (this.hovered.button || this.hovered.items)) return;

    this.open = false;
    // Block keyboard access immediately so links aren't reachable during the fade-out transition
    this.panel.setAttribute('inert', '');
    this.panel.classList.remove("is-visible");

    if (instant) {
      this.details.open = false;
    } else {
      // Only act on the opacity transition — the panel also transitions transform
      // and visibility, and we don't want to close `details` on whichever fires first.
      const onEnd = (e) => {
        if (e.propertyName !== "opacity") return;
        this.panel.removeEventListener("transitionend", onEnd);
        if (!this.open) this.details.open = false;
      };
      this.panel.addEventListener("transitionend", onEnd);
    }
  }

  forceClose() {
    this._clearShowTimeout();
    this._clearHideTimeout();
    this.hovered.button = false;
    this.hovered.items = false;
    if (this.open) this.hide(true);
  }
}

class Navigation {
  constructor() {
    this.storeHeader = document.querySelector("[data-store-header]");
    this.headerContainer = this.storeHeader?.parentElement;
    this.dockedNav = document.querySelector("[data-docked-navigation]");
    // Boolean, not a node — collection.js morphs that node on every filter apply.
    this.hasStickyFilterBar = Boolean(document.querySelector("[data-sticky-filter-bar]"));
    this.menus = [];
    this.lastScrollTop = 0;
    this.scrollThreshold = document.documentElement.clientHeight * 0.15;
    this._headerAnimation = "showSearchBar"; // header starts visible
    this._headerHasFocus = false;
  }

  init() {
    if (!this.dockedNav) return;

    // Initialize dropdown menus from <details> elements rendered by _navigation-item.liquid
    this.dockedNav.querySelectorAll("details").forEach(details => {
      const menu = new DesktopMenu(details, this);
      if (menu.panel) this.menus.push(menu);
    });

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAll();
    });

    // Touch device: close all menus when tapping outside the nav
    if (isTouchCapable) {
      document.addEventListener('touchstart', (e) => {
        if (window.innerWidth >= SCREENS.nav && !e.target.closest('[data-docked-navigation]')) {
          this.closeAll();
        }
      }, { passive: true });
    }

    // Scroll-based header hide/show
    if (this.headerContainer) {
      this._boundScroll = this._onScroll.bind(this);
      document.addEventListener("scroll", this._boundScroll, { passive: true });

      // Hidden header tab stops are still reachable on PLPs — bring it back on focus.
      // Scoped to [data-page-header], not headerContainer: drawer overlays also live in
      // headerContainer, so a cart open counted as "header focused" and slid the header back.
      // _headerHasFocus blocks _onScroll from re-hiding on the scroll that focus triggers.
      this._pageHeaderEls = [...this.headerContainer.querySelectorAll("[data-page-header]")];
      this._boundHeaderFocusIn = () => {
        this._headerHasFocus = true;
        this._setHeaderAnimation("showSearchBar");
      };
      this._boundHeaderFocusOut = (e) => {
        const next = e.relatedTarget;
        const stillInHeader = next && this._pageHeaderEls.some(bar => bar.contains(next));
        if (stillInHeader) return;
        this._headerHasFocus = false;
        this._onScroll();
      };
      this._pageHeaderEls.forEach(bar => {
        bar.addEventListener("focusin", this._boundHeaderFocusIn);
        bar.addEventListener("focusout", this._boundHeaderFocusOut);
      });

      // No scroll event on mid-page reload; catch the already-scrolled state now.
      if (window.scrollY > 0) this._onScroll();

      // Show header when filter bar hides at desktop width — no scroll event fires on resize.
      if (this.hasStickyFilterBar) {
        this._boundResize = () => {
          if (window.innerWidth >= SCREENS.nav) this._setHeaderAnimation("showSearchBar");
          else this._onScroll();
        };
        window.addEventListener("resize", this._boundResize, { passive: true });
      }
    }

    // Track when banner scrolls out of view so siblings can style against docked nav state.
    // Default to docked so header styling applies even when store-messages section is absent.
    document.body.dataset.stateDockedNav = "docked";
    const banner = document.querySelector("[data-section-type='store-messages']");
    if (banner) {
      this._bannerObserver = new IntersectionObserver(
        ([e]) => {
          document.body.dataset.stateDockedNav = e.intersectionRatio < 1 ? "docked" : "undocked";
        },
        { threshold: 1 },
      );
      this._bannerObserver.observe(banner);
    }
  }

  _onScroll() {
    // Scroll lock fires spurious events (scrollY jumps to/from 0); skip them.
    if (isScrollLocked()) return;

    const scrollTop = window.scrollY;
    const innerWidth = window.innerWidth;

    // PLP: yield the top strip immediately on any scroll — the bar pins on the same first pixel.
    // A gap between thresholds leaves both stickies overlapping.
    if (this.hasStickyFilterBar && innerWidth < SCREENS.nav) {
      if (scrollTop <= 0) {
        this._setHeaderAnimation("showSearchBar");
      } else if (!this._headerHasFocus) {
        this._setHeaderAnimation("hideSearchBar");
      }
      this.lastScrollTop = scrollTop;
      return;
    }

    const isMobile = innerWidth <= 640;
    const trigger = isMobile ? 640 : 0;
    const distance = Math.abs(this.lastScrollTop - scrollTop);

    if (scrollTop >= trigger && distance > this.scrollThreshold) {
      const animation = scrollTop > this.lastScrollTop ? "hideSearchBar" : "showSearchBar";
      this._setHeaderAnimation(animation);
      this.lastScrollTop = scrollTop;
    }

    // Reset on mobile when near top
    if (isMobile && scrollTop < trigger) {
      this._setHeaderAnimation("showSearchBar");
    }
  }

  /**
   * Apply a header show/hide animation, skipping the write when it is already applied —
   * the PLP branch runs on every scroll event and re-assigning would restart the slide.
   *
   * @param {"hideSearchBar"|"showSearchBar"} name
   */
  _setHeaderAnimation(name) {
    if (this._headerAnimation === name) return;
    this._headerAnimation = name;
    this.headerContainer.style.animation = `${name} 0.5s both`;
  }

  destroy() {
    if (this._bannerObserver) {
      this._bannerObserver.disconnect();
      this._bannerObserver = null;
    }
    if (this._boundScroll) {
      document.removeEventListener("scroll", this._boundScroll);
      this._boundScroll = null;
    }
    if (this._boundResize) {
      window.removeEventListener("resize", this._boundResize);
      this._boundResize = null;
    }
    if (this._boundHeaderFocusIn) {
      this._pageHeaderEls?.forEach(bar => {
        bar.removeEventListener("focusin", this._boundHeaderFocusIn);
        bar.removeEventListener("focusout", this._boundHeaderFocusOut);
      });
      this._boundHeaderFocusIn = null;
      this._boundHeaderFocusOut = null;
      this._pageHeaderEls = null;
    }
  }

  closeAllExcept(exception) {
    const hadOpen = this.menus.some(m => m !== exception && m.open);
    this.menus.forEach(m => m !== exception && m.forceClose());
    return hadOpen;
  }

  closeAll() {
    this.menus.forEach(m => {
      m._clearHideTimeout();
      m._clearShowTimeout();
      m.hovered.button = false;
      m.hovered.items = false;
      if (m.open) m.hide(false);
    });
  }
}

// Initialize
new Navigation().init();
